import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, BookOpen, ClipboardCheck, CheckCircle2 } from "lucide-react";

export const YEAR_COLORS = {
  1: { border: "border-cyan-400/40", text: "text-cyan-300", bg: "bg-cyan-500/10" },
  2: { border: "border-fuchsia-400/40", text: "text-fuchsia-300", bg: "bg-fuchsia-500/10" },
  3: { border: "border-amber-400/40", text: "text-amber-300", bg: "bg-amber-500/10" },
};

const MONTH_ORDER = ["IX", "X", "XI", "XII", "I", "II", "III", "IV", "V", "VI"];
export const isTestLesson = (l) => /^(test|testing)/i.test(l.title);

export function YearTabs({ years, summary, progress, year, onSelect }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="year-tabs">
      {years.map((y) => {
        const c = YEAR_COLORS[y.year];
        const active = year === y.year;
        const py = progress?.by_year.find((p) => p.year === y.year);
        const pct = py ? Math.round((py.read.length / py.total) * 100) : 0;
        return (
          <button key={y.year} onClick={() => onSelect(y.year)} data-testid={`year-tab-${y.year}`}
            className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${active ? `${c.border} ${c.bg} ${c.text}` : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            <span>Year {y.year}</span>
            <span className="font-mono-num ml-2 text-xs opacity-70">{py ? `${py.read.length}/${py.total}` : `${summary[y.year].lessons} lessons`}</span>
            {py && py.total > 0 && (
              <span className="ml-2 inline-block h-1.5 w-16 overflow-hidden rounded-full bg-white/10 align-middle">
                <span className={`block h-full ${active ? "bg-current" : "bg-cyan-400"}`} style={{ width: `${pct}%` }} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function CoverageBadge({ py }) {
  if (!py) return <span className="shrink-0 text-right text-[11px] leading-tight text-slate-500">Sign in<br />to track</span>;
  const pct = Math.round((py.read.length / py.total) * 100);
  return <span className="font-mono-num shrink-0 rounded-md border border-lime-400/40 bg-lime-500/10 px-2 py-1 text-xs text-lime-200 sm:px-3 sm:text-sm">{pct}% · {py.read.length}/{py.total}</span>;
}

const cellClass = (isRead, isTest) => {
  if (isRead) return "border-lime-400/60 bg-lime-500/40 hover:bg-lime-500/60";
  if (isTest) return "border-amber-400/30 bg-amber-500/10 hover:bg-amber-500/20";
  return "border-white/10 bg-white/5 hover:bg-white/10";
};

export function CoverageHeatmap({ year, units, readSet, py }) {
  const byMonth = {};
  units.forEach((u) => u.lessons.forEach((l) => (byMonth[l.month] = byMonth[l.month] || []).push(l)));
  return (
    <section className="rounded-md border border-white/10 bg-black/40 p-4 sm:p-6" data-testid={`heatmap-${year}`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Coverage · Year {year}</div>
          <h2 className="font-display text-base font-semibold text-white sm:text-lg">Where you stand this year</h2>
        </div>
        <CoverageBadge py={py} />
      </div>
      <div className="space-y-2">
        {MONTH_ORDER.filter((m) => byMonth[m]).map((m) => (
          <div key={m} className="flex items-center gap-3" data-testid={`heatmap-row-${m}`}>
            <div className="font-mono-num w-8 text-xs text-slate-500">{m}</div>
            <div className="flex flex-wrap gap-1">
              {byMonth[m].map((l) => (
                <Link key={l.order} to={`/curriculum/${year}/${l.order}`} data-testid={`heatmap-cell-${year}-${l.order}`} title={`#${l.order} · ${l.title}`}
                  className={`h-6 w-6 rounded-sm border ${cellClass(readSet.has(l.order), isTestLesson(l))}`} />
              ))}
            </div>
          </div>
        ))}
        <div className="mt-3 flex items-center gap-4 text-[10px] uppercase tracking-widest text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-lime-400/60 bg-lime-500/40" /> Read</span>
          <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-amber-400/30 bg-amber-500/10" /> Test</span>
          <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-white/10 bg-white/5" /> Not yet</span>
        </div>
      </div>
    </section>
  );
}

function LessonRow({ year, l, isRead }) {
  const isTest = isTestLesson(l);
  const Icon = isRead ? CheckCircle2 : isTest ? ClipboardCheck : BookOpen;
  const border = isRead ? "border-lime-400/40 bg-lime-500/5" : isTest ? "border-amber-400/30 bg-black/60" : "border-white/10 bg-black/60";
  const iconTone = isRead ? "text-lime-300" : isTest ? "text-amber-300" : "text-slate-400";
  return (
    <Link to={`/curriculum/${year}/${l.order}`} data-testid={`lesson-${year}-${l.order}`}
      className={`group flex items-center justify-between gap-2 rounded-md border px-3 py-2.5 text-sm transition-colors hover:border-cyan-400/40 hover:bg-cyan-500/5 sm:px-4 sm:py-3 ${border}`}>
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <span className="font-mono-num w-10 shrink-0 text-[10px] text-slate-500 sm:w-8 sm:text-xs">{l.month}·{String(l.order).padStart(2, "0")}</span>
        <Icon size={14} className={`shrink-0 ${iconTone}`} />
        <span className={`truncate ${isRead ? "text-lime-100" : "text-slate-100"}`}>{l.title}</span>
      </div>
      <ChevronRight size={14} className="shrink-0 text-slate-500 transition-transform group-hover:translate-x-1 group-hover:text-cyan-300" />
    </Link>
  );
}

export function UnitSection({ year, unit, index, readSet }) {
  return (
    <section className="rounded-md border border-white/10 bg-black/40 p-4 sm:p-6" data-testid={`unit-${year}-${index}`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className={`font-display text-lg font-semibold sm:text-xl ${YEAR_COLORS[year].text}`}>{unit.unit}</h2>
        <div className="shrink-0 text-[10px] uppercase tracking-widest text-slate-500">{unit.lessons.length} lessons</div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
        {unit.lessons.map((l) => <LessonRow key={l.order} year={year} l={l} isRead={readSet.has(l.order)} />)}
      </div>
    </section>
  );
}
